import { useState, useEffect, useCallback, useRef } from 'react'
import reportService from '@services/reportService'

/**
 * useReportData Hook
 *
 * Custom hook for fetching and managing report data.
 * Provides loading state, error handling, and refresh functionality.
 *
 * @param {string} reportType - Report type identifier
 * @param {Object} options - Hook options
 * @param {Function} options.fetcher - Custom fetch function
 * @param {Object} options.initialFilters - Initial filter values
 * @param {boolean} options.fetchOnMount - Fetch data on mount (default: true)
 * @param {number} options.pollingInterval - Auto-refresh interval in ms (0 = disabled)
 * @returns {Object} Report data state and handlers
 */
export const useReportData = (reportType, options = {}) => {
	const {
		fetcher,
		initialFilters = {},
		fetchOnMount = true,
		pollingInterval = 0,
	} = options

	const [data, setData] = useState(null)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const [filters, setFilters] = useState(initialFilters)
	const [lastUpdated, setLastUpdated] = useState(null)

	const pollingRef = useRef(null)
	const mountedRef = useRef(true)

	// Default fetcher based on report type
	const defaultFetcher = useCallback(async (params) => {
		const fetcherMap = {
			// Platform reports
			platform_overview: reportService.getPlatformOverview,
			platform_companies: reportService.getPlatformCompanies,
			platform_revenue: reportService.getPlatformRevenue,
			platform_activity: reportService.getPlatformActivity,
			platform_projects: reportService.getPlatformProjects,
			platform_tasks: reportService.getPlatformTasks,
			platform_health: reportService.getPlatformHealth,

			// Company reports
			company_overview: reportService.getCompanyOverview,
			company_projects: reportService.getCompanyProjects,
			company_compliance: reportService.getCompanyCompliance,
			company_team: reportService.getCompanyTeam,
			company_sla: reportService.getCompanySLA,
			company_tasks: reportService.getCompanyTasks,
			company_workflows: reportService.getCompanyWorkflows,
			company_audit: reportService.getCompanyAudit,
			company_documents: reportService.getCompanyDocuments,
			gap_analysis: reportService.getGapAnalysis,
			senior_management: reportService.getSeniorManagementReport,
			compliance_manager: reportService.getComplianceManagerReport,

			// User reports
			my_tasks: reportService.getMyTasks,
			my_approvals: reportService.getMyApprovals,
			my_performance: reportService.getMyPerformance,
		}

		const fetchFn = fetcherMap[reportType]
		if (!fetchFn) {
			throw new Error(`Unknown report type: ${reportType}`)
		}

		return fetchFn(params)
	}, [reportType])

	// Fetch data
	const fetchData = useCallback(async (customFilters = null) => {
		const activeFilters = customFilters || filters

		setLoading(true)
		setError(null)

		try {
			const fetchFn = fetcher || defaultFetcher
			const response = await fetchFn(activeFilters)

			if (mountedRef.current) {
				setData(response.data || response)
				setLastUpdated(new Date())
			}
		} catch (err) {
			if (mountedRef.current) {
				setError(err.message || 'Failed to fetch report data')
				console.error('Report fetch error:', err)
			}
		} finally {
			if (mountedRef.current) {
				setLoading(false)
			}
		}
	}, [filters, fetcher, defaultFetcher])

	// Refresh data
	const refresh = useCallback(() => {
		return fetchData()
	}, [fetchData])

	// Update filters
	const updateFilters = useCallback((newFilters) => {
		setFilters((prev) => ({ ...prev, ...newFilters }))
	}, [])

	// Reset filters
	const resetFilters = useCallback(() => {
		setFilters(initialFilters)
	}, [initialFilters])

	// Apply filters and fetch
	const applyFilters = useCallback((newFilters) => {
		const updatedFilters = { ...filters, ...newFilters }
		setFilters(updatedFilters)
		return fetchData(updatedFilters)
	}, [filters, fetchData])

	// Fetch on mount
	useEffect(() => {
		if (fetchOnMount) {
			fetchData()
		}
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	// Polling
	useEffect(() => {
		if (pollingInterval > 0) {
			pollingRef.current = setInterval(() => {
				fetchData()
			}, pollingInterval)
		}

		return () => {
			if (pollingRef.current) {
				clearInterval(pollingRef.current)
			}
		}
	}, [pollingInterval, fetchData])

	// Cleanup
	useEffect(() => {
		mountedRef.current = true
		return () => {
			mountedRef.current = false
		}
	}, [])

	return {
		data,
		loading,
		error,
		filters,
		lastUpdated,
		refresh,
		updateFilters,
		resetFilters,
		applyFilters,
		fetchData,
	}
}

/**
 * useProjectReport Hook
 *
 * Specialized hook for project-specific reports.
 *
 * @param {number} projectId - Project ID
 * @param {string} reportType - Report type (detail, timeline, tasks, team, compliance)
 * @param {Object} options - Hook options
 * @returns {Object} Report data state and handlers
 */
export const useProjectReport = (projectId, reportType = 'detail', options = {}) => {
	const fetcher = useCallback(async (filters) => {
		switch (reportType) {
			case 'detail':
				return reportService.getProjectDetail(projectId, filters)
			case 'timeline':
				return reportService.getProjectTimeline(projectId)
			case 'tasks':
				return reportService.getProjectTasks(projectId, filters)
			case 'team':
				return reportService.getProjectTeam(projectId)
			case 'compliance':
				return reportService.getProjectCompliance(projectId)
			default:
				throw new Error(`Unknown project report type: ${reportType}`)
		}
	}, [projectId, reportType])

	return useReportData(`project_${reportType}`, { ...options, fetcher })
}

/**
 * useUserReport Hook
 *
 * Specialized hook for user-specific reports.
 *
 * @param {number} userId - User ID (null for current user)
 * @param {string} reportType - Report type (summary, performance)
 * @param {Object} options - Hook options
 * @returns {Object} Report data state and handlers
 */
export const useUserReport = (userId = null, reportType = 'summary', options = {}) => {
	const fetcher = useCallback(async (filters) => {
		if (userId) {
			switch (reportType) {
				case 'summary':
					return reportService.getUserSummary(userId)
				case 'performance':
					return reportService.getUserPerformance(userId, filters)
				default:
					throw new Error(`Unknown user report type: ${reportType}`)
			}
		} else {
			switch (reportType) {
				case 'tasks':
					return reportService.getMyTasks(filters)
				case 'approvals':
					return reportService.getMyApprovals(filters)
				case 'performance':
					return reportService.getMyPerformance(filters)
				default:
					throw new Error(`Unknown user report type: ${reportType}`)
			}
		}
	}, [userId, reportType])

	return useReportData(`user_${reportType}`, { ...options, fetcher })
}

/**
 * useReportExport Hook
 *
 * Hook for handling report exports.
 *
 * @returns {Object} Export state and handlers
 */
export const useReportExport = () => {
	const [exporting, setExporting] = useState(false)
	const [exportError, setExportError] = useState(null)

	const exportReport = useCallback(async (reportType, format, filters = {}, config = {}) => {
		setExporting(true)
		setExportError(null)

		try {
			await reportService.exportReport(reportType, format, filters, config)
			return true
		} catch (err) {
			setExportError(err.message || 'Export failed')
			console.error('Export error:', err)
			return false
		} finally {
			setExporting(false)
		}
	}, [])

	const exportToFile = useCallback(async (reportType, format, filters = {}, config = {}) => {
		setExporting(true)
		setExportError(null)

		try {
			const response = await reportService.exportToFile(reportType, format, filters, config)
			return response.data
		} catch (err) {
			setExportError(err.message || 'Export failed')
			console.error('Export error:', err)
			return null
		} finally {
			setExporting(false)
		}
	}, [])

	return {
		exporting,
		exportError,
		exportReport,
		exportToFile,
	}
}

export default useReportData
